import React, {
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useReducer,
	useRef,
} from 'react';
import {FixedSizeList, VariableSizeList} from 'react-window';
import {useDocumentKeyDown} from '@netx/core-components/hooks/useDocumentKeyDown';
import {useEnhancedRef} from '@netx/core-components/hooks/useEnhancedRef';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import {
	createSyntheticEvent,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';
import {
	Action,
	getInitialState,
	InternalContext,
	InternalContextDescriptor,
	PublicContext,
	PublicContextDescriptor,
	reducer,
} from '../../store';

const FOCUS_TRAP_NAVIGATION_KEYS = [
	'ArrowDown',
	'ArrowUp',
	'End',
	'Home',
	'PageDown',
	'PageUp',
] as const;

const FOCUS_TRAP_NAVIGATION = {
	ArrowDown: 'NextItem',
	ArrowUp: 'PrevItem',
	End: 'End',
	Home: 'Home',
	PageDown: 'NextPage',
	PageUp: 'PrevPage',
} as const;

export type ArrowFocusTrapContextProps = {
	children:
		| React.ReactNode
		| ((context: InternalContextDescriptor) => React.ReactNode);
	disabled?: boolean;
	restoreFocusOnEnable?: boolean;
	id?: string;
	initialActiveIndex?: number;
	activeDescendantLabel?: string;
	pageSize?: number;
	returnToIndexOnFocus?: number;
	total: number;
	virtualListRef?: React.RefObject<FixedSizeList | VariableSizeList | null>;
	onActiveIndexChange?: (index: number) => void;
	onItemClick?: (
		event: React.MouseEvent<HTMLElement, MouseEvent>,
		index: number,
	) => void;
	onItemKeyDown?: (
		event: React.KeyboardEvent<HTMLElement>,
		index: number,
	) => void;
};

export const ArrowFocusTrapContext = memo(
	React.forwardRef<InternalContextDescriptor, ArrowFocusTrapContextProps>(
		function ArrowFocusTrapContext(
			{
				id,
				activeDescendantLabel,
				disabled: _disabled,
				initialActiveIndex,
				pageSize,
				restoreFocusOnEnable,
				onActiveIndexChange,
				onItemClick,
				onItemKeyDown,
				returnToIndexOnFocus,
				total,
				children,
				virtualListRef,
			},
			ref,
		) {
			const store = useReducer(
				reducer,
				{
					activeIndex: initialActiveIndex != null ? initialActiveIndex : 0,
					pageSize: pageSize || 25,
					returnToIndexOnFocus: returnToIndexOnFocus || -1,
					total,
				},
				getInitialState,
			);

			const [state, dispatch] = store;
			const {
				keysDisabled,
				clickKeysDisabled,
				disabled,
				userDisabled,
				activeIndex,
				returnToIndexOnFocus: _returnToIndexOnFocus,
			} = state;

			const [stateRef] = useEnhancedRef(state);
			const [activeIndexRef] = useEnhancedRef(activeIndex);
			const [disabledRef] = useEnhancedRef(disabled);
			const [userDisabledRef] = useEnhancedRef(userDisabled);
			const justClickedItem = useRef(false);
			const sendNextFocusToPreviousElement = useRef(false);
			const justUpdatedIndexFromKeyboard = useRef(false);

			useWhenChangedEffect(state.activeIndex, (activeIndex) => {
				onActiveIndexChange?.(activeIndex);
			});

			useEffect(() => {
				dispatch({type: Action.SetTotal, total});
			}, [total]);

			const lastRestoreFocusOnEnable = useRef(restoreFocusOnEnable === true);
			useWhenChangedEffect(_disabled, (disabled) => {
				if (disabled === true) {
					lastRestoreFocusOnEnable.current = restoreFocusOnEnable === true;
					if (restoreFocusOnEnable) {
						dispatch({type: Action.UserDisable});
						dispatch({type: Action.Disable});
					} else {
						dispatch({type: Action.UserDisable});
					}
				} else if (disabled === false) {
					if (lastRestoreFocusOnEnable.current) {
						dispatch({type: Action.UserEnable});
						dispatch({type: Action.Enable});
					} else {
						dispatch({type: Action.UserEnable});
					}
				}
			});

			useEffect(() => {
				dispatch({
					type: Action.SetReturnToIndexOnFocus,
					index: returnToIndexOnFocus || -1,
				});
			}, [returnToIndexOnFocus]);

			useEffect(() => {
				if (
					!justUpdatedIndexFromKeyboard.current ||
					(virtualListRef?.current?.state as any)?.isScrolling
				)
					return;

				justUpdatedIndexFromKeyboard.current = false;
				virtualListRef?.current?.scrollToItem(activeIndex, 'smart');
			}, [activeIndex]);

			const itemClickedTimeout = useRef<NodeJS.Timeout>();
			const itemClicked = useCallback((index: number) => {
				//if (index === activeIndexRef.current) return;
				itemClickedTimeout.current && clearTimeout(itemClickedTimeout.current);
				justClickedItem.current = true;
				itemClickedTimeout.current = setTimeout(() => {
					justClickedItem.current = false;
				}, 16);
			}, []);

			const disableNextScrollUpdate = useRef(false);
			const handleScrollIndexUpdate = useCallback(
				(index: number, page: number, stopIndex: number) => {
					// TODO: we should store this info and only use it when refocusing if there is not a return to
					// but then this needs to know about return to...
					if (!justClickedItem.current) {
						const activeIndex = activeIndexRef.current;
						const disabled = disabledRef.current;
						if (activeIndex < index || activeIndex > stopIndex) {
							if (!disableNextScrollUpdate.current || disabled) {
								dispatch({type: Action.SetActiveItem, index});
							}
						}
					}
					disableNextScrollUpdate.current = false;
				},
				[],
			);

			const setActiveIndex = useCallback((index: number) => {
				dispatch({type: Action.SetActiveItem, index});
			}, []);

			const handleItemClick = useCallback(
				(index: number) =>
					(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
						setActiveIndex(index);
						onItemClick?.(event, index);
					},
				[onItemClick],
			);

			useDocumentKeyDown(
				useCallback(
					(event) => {
						if (event.defaultPrevented) return;

						const {activeIndex, clickKeysDisabled, keysDisabled} =
							stateRef.current;
						// TODO: rename/make an action for signifying that actions are focused
						// then we can make a better decision on how to handle things/decouple
						// keys being disabled from actions (as there may be other cases where
						// we want to disable keys when not dealing with actions)
						if (!clickKeysDisabled) {
							switch (event.key) {
								case 'Tab': {
									disableNextScrollUpdate.current = true;
									sendNextFocusToPreviousElement.current = event.shiftKey;
								}
							}
						}

						if (!keysDisabled && !userDisabledRef.current) {
							if (!clickKeysDisabled && activeIndex > -1) {
								onItemKeyDown?.(
									createSyntheticEvent<HTMLElement, KeyboardEvent>(
										event,
									) as React.KeyboardEvent<HTMLElement>,
									activeIndex,
								);
							}

							if (!FOCUS_TRAP_NAVIGATION_KEYS.includes(event.key as any))
								return;

							disableNextScrollUpdate.current = true;
							// @ts-ignore
							dispatch({type: Action[FOCUS_TRAP_NAVIGATION[event.key]]});
							justUpdatedIndexFromKeyboard.current = true;

							return false;
						}
					},
					[onItemKeyDown],
				),
				{
					disabled,
				},
			);

			const handleItemInteractStart = useCallback(
				(index: number) =>
					(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
						if (
							!disabledRef.current ||
							!userDisabledRef.current ||
							event.ctrlKey ||
							event.button === 2
						)
							return;
						itemClicked(index);
						dispatch({type: Action.SetActiveItem, index});
					},
				[],
			);

			const setReturnToIndexOnFocus = useCallback((index: number) => {
				dispatch({type: Action.SetReturnToIndexOnFocus, index});
			}, []);

			const publicContext = useMemo(() => {
				const activeDescendant =
					!disabled && activeIndex > -1 && !userDisabled
						? `${activeDescendantLabel}-${activeIndex.toString()}`
						: undefined;

				const context: PublicContextDescriptor = {
					activeIndex,
					activeDescendant,
					disableNextScrollUpdate,
					disabled,
					justClickedItem,
					userDisabled,
					virtualListRef,
					handleItemClick,
					handleItemInteractStart,
					handleScrollIndexUpdate,
					setActiveIndex,
				};

				return context;
			}, [
				activeDescendantLabel,
				activeIndex,
				disabled,
				handleItemClick,
				handleItemInteractStart,
				handleScrollIndexUpdate,
				setActiveIndex,
			]);

			const internalContext = useMemo(() => {
				const activeDescendant =
					!disabled && activeIndex > -1 && !userDisabled
						? `${activeDescendantLabel}-${activeIndex.toString()}`
						: undefined;

				const context: InternalContextDescriptor = {
					activeDescendant,
					activeIndex,
					clickKeysDisabled,
					disableNextScrollUpdate,
					disabled,
					userDisabled,
					justClickedItem,
					keysDisabled,
					returnToIndexOnFocus: _returnToIndexOnFocus,
					sendNextFocusToPreviousElement,
					virtualListRef,
					dispatch,
					handleItemClick,
					handleItemInteractStart,
					handleScrollIndexUpdate,
					setActiveIndex,
					setReturnToIndexOnFocus,
				};

				return context;
			}, [
				activeIndex,
				clickKeysDisabled,
				disabled,
				keysDisabled,
				_returnToIndexOnFocus,
				dispatch,
				handleItemClick,
				handleItemInteractStart,
				handleScrollIndexUpdate,
				setActiveIndex,
				setReturnToIndexOnFocus,
			]);

			useImperativeHandle(ref, () => internalContext, [internalContext]);

			return (
				<InternalContext.Provider value={internalContext}>
					<PublicContext.Provider value={publicContext}>
						{renderChildrenOrChildrenFunction(children, internalContext)}
					</PublicContext.Provider>
				</InternalContext.Provider>
			);
		},
	),
);

export default ArrowFocusTrapContext;
